import React, { useState } from 'react'
import { Component, LocaleCode } from '../types/component'

interface CheckoutProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
  cartId?: string | null
}

export function Checkout({
  component,
  storeId,
  apiUrl,
  style,
  cartId,
}: CheckoutProps) {
  const config = component.config as any
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const buttonText = config.buttonText || 'Proceed to Checkout'
  const buttonStyle = config.buttonStyle || 'primary'

  const handleCheckout = async () => {
    if (!cartId || !storeId) {
      setError('Please add items to your cart first')
      return
    }

    try {
      setIsRedirecting(true)
      setError(null)

      // Get cart to generate checkout URL
      const response = await fetch(
        `${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${cartId}`
      )
      const cartResponse = await response.json()
      const cart = cartResponse.data

      // BigCommerce provides checkout URL in cart response
      if (cart && cart.redirect_urls && cart.redirect_urls.checkout_url) {
        // Redirect to BigCommerce hosted checkout
        window.location.href = cart.redirect_urls.checkout_url
      } else {
        setError('Unable to generate checkout URL. Please try again.')
        setIsRedirecting(false)
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError('Failed to proceed to checkout. Please try again.')
      setIsRedirecting(false)
    }
  }

  const getButtonStyles = (): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: '12px 24px',
      borderRadius: '6px',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      transition: 'all 0.2s',
      opacity: isRedirecting || !cartId ? 0.5 : 1,
      pointerEvents: isRedirecting || !cartId ? 'none' : 'auto',
    }

    switch (buttonStyle) {
      case 'primary':
        return {
          ...baseStyle,
          backgroundColor: '#3b82f6',
          color: '#ffffff',
        }
      case 'secondary':
        return {
          ...baseStyle,
          backgroundColor: '#6b7280',
          color: '#ffffff',
        }
      case 'outline':
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          color: '#3b82f6',
          border: '2px solid #3b82f6',
        }
      case 'ghost':
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          color: '#374151',
        }
      default:
        return {
          ...baseStyle,
          backgroundColor: '#3b82f6',
          color: '#ffffff',
        }
    }
  }

  const containerStyle: React.CSSProperties = {
    ...style,
  }

  return (
    <div style={containerStyle}>
      <button
        onClick={handleCheckout}
        disabled={isRedirecting || !cartId}
        style={getButtonStyles()}
        onMouseEnter={(e) => {
          if (!isRedirecting && cartId) {
            e.currentTarget.style.opacity = '0.9'
          }
        }}
        onMouseLeave={(e) => {
          if (!isRedirecting && cartId) {
            e.currentTarget.style.opacity = '1'
          }
        }}
      >
        {isRedirecting ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg
              style={{
                animation: 'spin 1s linear infinite',
                height: '20px',
                width: '20px',
              }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Redirecting...
          </span>
        ) : (
          buttonText
        )}
      </button>
      {error && (
        <p style={{ color: '#ef4444', marginTop: '8px', fontSize: '14px' }}>
          {error}
        </p>
      )}
    </div>
  )
}
